import { ChipProps } from '@mui/material/Chip';
import { Components, Palette, Theme } from '@mui/material/styles';
import { palette } from 'theme/palette';

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    secondary: false;
    error: false;
    warning: false;
    info: true;
    success: true;
    failed: true;

    pink: true;
    indigo: true;
    teal: true;
    green: true;
    amber: true;
    blue: true;
    grey: true;
  }

  interface ChipPropsSizeOverrides {
    large: true;
  }

  interface ChipClasses {
    sizeLarge: string;
  }
}

export const CHIPS_HEIGHTS_MAP = {
  SMALL: 18,
  MEDIUM: 24,
  LARGE: 32,
};

export const CHIPS_PADDINGS_HORIZONTAL = {
  SMALL: 8,
  MEDIUM: 8,
  LARGE: 10,
};

const chipPalette: Record<
  Exclude<ChipProps['color'], undefined>,
  Omit<Palette['primary'], 'dark' | 'light'> & {
    iconBackground: string;
    iconBackgroundOutlined: string;
    border: string;
    outlinedContrastText: string;
  }
> = {
  pink: {
    main: palette.pink['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.pink['600'],
    border: palette.pink['600'],
    outlinedContrastText: palette.pink['600'],
  },
  indigo: {
    main: palette.indigo['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.indigo['600'],
    border: palette.indigo['600'],
    outlinedContrastText: palette.indigo['600'],
  },
  teal: {
    main: palette.teal['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.teal['600'],
    border: palette.teal['600'],
    outlinedContrastText: palette.teal['600'],
  },
  green: {
    main: palette.green['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.green['600'],
    border: palette.green['600'],
    outlinedContrastText: palette.green['600'],
  },
  amber: {
    main: palette.amber['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.amber['600'],
    border: palette.amber['600'],
    outlinedContrastText: palette.amber['600'],
  },
  blue: {
    main: palette.blue['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.blue['600'],
    border: palette.blue['600'],
    outlinedContrastText: palette.blue['600'],
  },
  grey: {
    main: palette.grey['600'],
    contrastText: palette.text.white,
    iconBackground: palette.text.white,
    iconBackgroundOutlined: palette.grey['600'],
    border: palette.grey['600'],
    outlinedContrastText: palette.grey['600'],
  },
  primary: {
    main: '#1976D2',
    contrastText: palette.text.white,
    outlinedContrastText: '#1976D2',
    iconBackground: palette.text.white,
    iconBackgroundOutlined: '#1976D2',
    border: '#1976D2',
  },
  default: {
    main: palette.border,
    contrastText: palette.text.primary,
    outlinedContrastText: palette.text.primary,
    iconBackground: palette.text.secondary,
    iconBackgroundOutlined: palette.text.secondary,
    border: palette.text.secondary,
  },
  info: {
    main: palette.blue['100'],
    contrastText: palette.blue['600'],
    outlinedContrastText: palette.blue['600'],
    iconBackground: palette.blue['600'],
    iconBackgroundOutlined: palette.blue['600'],
    border: palette.blue['600'],
  },
  failed: {
    main: palette.red['100'],
    contrastText: palette.red['600'],
    outlinedContrastText: palette.red['600'],
    iconBackground: palette.red['600'],
    iconBackgroundOutlined: palette.red['600'],
    border: palette.red['600'],
  },
  success: {
    main: palette.green['100'],
    contrastText: palette.green['600'],
    outlinedContrastText: palette.green['600'],
    iconBackground: palette.green['600'],
    iconBackgroundOutlined: palette.green['600'],
    border: palette.green['600'],
  },
};

export const MuiChip: Components<Theme>['MuiChip'] = {
  defaultProps: {
    color: 'default',
  },
  styleOverrides: {
    root: ({ ownerState }) => {
      const { variant, color } = ownerState;

      const usedColor = chipPalette[color || 'default'];

      if (variant === 'outlined') {
        return {
          backgroundColor: palette.text.white,
          color: usedColor.outlinedContrastText,
          border: `1px solid ${usedColor.border}`,
        };
      }

      return {
        backgroundColor: usedColor.main,
        color: usedColor.contrastText,
      };
    },
    deletable: ({ ownerState, theme }) => {
      const { size } = ownerState;

      if (size === 'small') {
        return {
          paddingRight: 0,
        };
      }

      if (size === 'large') {
        return {
          paddingRight: theme.spacing(6),
        };
      }

      return {
        paddingRight: theme.spacing(3),
      };
    },
    label: () => {
      return {
        paddingLeft: 0,
        paddingRight: 0,
      };
    },
    sizeSmall: ({ theme }) => ({
      height: theme.spacing(CHIPS_HEIGHTS_MAP.SMALL),
      paddingLeft: theme.spacing(CHIPS_PADDINGS_HORIZONTAL.SMALL),
      paddingRight: theme.spacing(CHIPS_PADDINGS_HORIZONTAL.SMALL),
    }),
    sizeMedium: ({ theme }) => ({
      height: theme.spacing(CHIPS_HEIGHTS_MAP.MEDIUM),
      paddingLeft: theme.spacing(CHIPS_PADDINGS_HORIZONTAL.MEDIUM),
      paddingRight: theme.spacing(CHIPS_PADDINGS_HORIZONTAL.MEDIUM),
      borderRadius: theme.spacing(16),
    }),
    sizeLarge: ({ theme }) => ({
      height: theme.spacing(CHIPS_HEIGHTS_MAP.LARGE),
      paddingLeft: theme.spacing(CHIPS_PADDINGS_HORIZONTAL.LARGE),
      paddingRight: theme.spacing(CHIPS_PADDINGS_HORIZONTAL.LARGE),
    }),
    deleteIcon: ({ ownerState, theme }) => {
      const { size, variant, color } = ownerState;
      const usedColor = chipPalette[color || 'default'];

      const shared = {
        height: theme.spacing(16),
        width: 'auto',
        margin: 0,
        padding: theme.spacing(2),
        paddingLeft: theme.spacing(2),
      };

      if (size === 'large') {
        shared.paddingLeft = theme.spacing(4);
      }

      return {
        ...shared,
        fill: variant === 'filled' ? usedColor.iconBackground : usedColor.iconBackgroundOutlined,
      };
    },
  },
};
